/* © 2024-2026 Johannes Roob. Proprietärer Code. Keine Nutzung oder Weitergabe ohne Erlaubnis. | Proprietary code. No use or distribution without permission. */

import { mustMap } from "../../util/must.js";
import { Window } from "./Window.js";

export class Taskbar {
    #element: HTMLElement;
    #items: Map<Window, TaskbarItem> = new Map();
    #activeItem: TaskbarItem | undefined;

    constructor(element: HTMLElement) {
        this.#element = element;
    }

    getRect() {
        return this.#element.getBoundingClientRect();
    }

    create(window: Window) {
        const item = new TaskbarItem(window);
        this.#items.set(window, item);
        this.#element.appendChild(item.element);
    }

    get(window: Window) {
        return mustMap(this.#items, window);
    }

    delete(window: Window) {
        const item = this.get(window);
        this.unactivate(window);
        item.delete();
        this.#items.delete(window);
    }

    activate(window: Window) {
        const item = this.get(window);

        if (this.#activeItem === item) {
            return;
        }

        if (this.#activeItem !== undefined) {
            unactivateItem(this.#activeItem);
        }

        activateItem(item);
        this.#activeItem = item;
    }

    unactivate(window: Window) {
        const item = this.get(window);
        unactivateItem(item);

        if (this.#activeItem === item) {
            this.#activeItem = undefined;
        }
    }

    destroyAll() {
        for (const item of this.#items.values()) {
            item.delete();
        }

        this.#items.clear();
        this.#activeItem = undefined;
    }
}

function activateItem(item: TaskbarItem) {
    item.element.classList.add("Selected");
}

function unactivateItem(item: TaskbarItem) {
    item.element.classList.remove("Selected");
}

class TaskbarItem {
    #abortController: AbortController = new AbortController();
    #element: HTMLDivElement;

    constructor(w: Window) {
        const div = document.createElement("div");
        div.className = "NavigationViewItem TaskbarItem";
        div.textContent = w.title;

        div.addEventListener("click",
            () => {
                w.formManager.windowManager.toggle(w);
            },
            {
                signal: this.#abortController.signal
            }
        );

        this.#element = div;
    }

    get element() {
        return this.#element;
    }

    delete() {
        this.#abortController.abort();
        this.#element.remove();
    }
}